/* eslint-disable no-console */
import React, { Component, Suspense } from "react";
import { isChunkLoadError } from './utils/lazyWithRetry';

const ErrorScreenMC = React.lazy(() => import("./presentation/components/ErrorScreenMC"));

/**
 * Fallback mínimo cuando ErrorScreenMC no puede cargar.
 * Usa Tailwind para mantener consistencia visual.
 */
const StaticFallback = () => (
  <div className="tw-wrapper">
    <div 
      className="md:tw-ml-[60px] tw-min-h-[calc(100vh-60px)] tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-4"
      style={{ background: 'linear-gradient(180deg, #FFF 55.1%, #EAFEFF 100%)' }}
    >
      <h1 className="tw-text-3xl tw-font-semibold tw-text-blue-500 tw-mb-4 tw-text-center">
        Ups... ocurrió un error
      </h1>

      <p className="tw-text-gray-700 tw-mb-6 tw-text-base tw-text-center tw-max-w-lg">
        No pudimos cargar esta información. Intenta nuevamente o vuelve al Dashboard.
      </p>

      <div className="tw-flex tw-flex-row tw-gap-4 tw-items-center">
        <a
          href="/"
          className="tw-px-8 tw-py-3 tw-border tw-border-blue-500 tw-rounded-lg tw-text-blue-500 tw-text-sm tw-bg-white hover:tw-bg-blue-50"
        >
          Volver al Dashboard
        </a>
        <button
          onClick={() => window.location.reload()}
          type="button"
          className="tw-px-8 tw-py-3 tw-bg-blue-500 tw-rounded-lg tw-text-white tw-text-sm tw-border-none hover:tw-bg-blue-600 tw-cursor-pointer"
        >
          Recargar Página
        </button>
      </div>
    </div>
  </div>
);

/**
 * ErrorBoundary captures errors in child components and displays ErrorScreenMC.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    let errorMsg = '';
    if (error instanceof Error) {
      errorMsg = `${error.name}: ${error.message}\n${error.stack}`;
    } else if (typeof error === 'object') {
      try {
        errorMsg = JSON.stringify(error, null, 2);
      } catch (e) {
        errorMsg = String(error);
      }
    } else {
      errorMsg = String(error);
    }
    console.error("[ErrorBoundary] Error capturado:\n", errorMsg);
    if (errorInfo && errorInfo.componentStack) {
      console.error("[ErrorBoundary] Component stack:\n", errorInfo.componentStack);
    }
  }

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children } = this.props;
    const chunkLoadError = isChunkLoadError(error);
    const errorMessage = chunkLoadError
      ? 'La aplicacion se actualizo y no pudimos cargar una parte. Recarga la pagina para continuar.'
      : (error?.message || 'Unknown error');

    if (hasError) {
      return (
        <Suspense fallback={<StaticFallback />}>
          <ErrorScreenMC
            errorMessage={errorMessage}
            errorStack={errorInfo?.componentStack || error?.stack || ''}
            currentUrl={window.location.href}
          />
        </Suspense>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
